import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { HELP_DOC_THEME, HELP_DOC_TYPOGRAPHY } from './helpTheme';

interface HeadingItem {
  id: string;
  text: string;
  level: 2 | 3;
}

export interface HelpContentProps {
  title: string;
  summary?: string | null;
  body?: string | null;
}

const looksLikeHtml = (value?: string | null) => {
  if (!value) return false;
  return /<\s*(h1|h2|h3|p|ul|ol|li|div|section|article|table|pre|code|a)\b/i.test(value);
};

const toSafeHtml = (body?: string | null) => {
  if (!body) return '';
  if (looksLikeHtml(body)) return body;
  const html = body
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^### (.*)$/gm, '<h3>$1</h3>')
    .replace(/^## (.*)$/gm, '<h2>$1</h2>')
    .replace(/\n{2,}/g, '</p><p>')
    .replace(/\n/g, '<br />');

  return html.replace(/(?:^|<br \/>)(- .+?)(?=(<br \/>- )|<\/p>|$)/g, (match) => {
    if (!match.includes('- ')) return match;
    const lines = match
      .replace(/^<br \/>/, '')
      .split('<br />')
      .filter((line) => line.trim().startsWith('- '))
      .map((line) => `<li>${line.trim().replace(/^- /, '')}</li>`)
      .join('');
    return `<ul>${lines}</ul>`;
  });
};

const parseHeadings = (body?: string | null): HeadingItem[] => {
  if (!body) return [];
  if (looksLikeHtml(body)) {
    const headings: HeadingItem[] = [];
    const regex = /<h([23])[^>]*>(.*?)<\/h\1>/gi;
    let index = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(body)) !== null) {
      index += 1;
      const level = Number(match[1]) as 2 | 3;
      const text = match[2].replace(/<[^>]+>/g, '').trim();
      headings.push({ id: `help-section-${index}`, text, level });
    }
    return headings;
  }
  const headings: HeadingItem[] = [];
  const lines = body.split('\n');
  let index = 0;
  for (const line of lines) {
    const h2 = line.match(/^## (.+)$/);
    const h3 = line.match(/^### (.+)$/);
    if (h2) {
      index += 1;
      headings.push({ id: `help-section-${index}`, text: h2[1], level: 2 });
    } else if (h3) {
      index += 1;
      headings.push({ id: `help-section-${index}`, text: h3[1], level: 3 });
    }
  }
  return headings;
};

const injectHeadingIds = (html: string, headings: HeadingItem[]) => {
  let i = 0;
  return html.replace(/<(h[23])([^>]*)>(.*?)<\/\1>/g, (_, tag, attrs, content) => {
    const next = headings[i];
    i += 1;
    if (!next) return `<${tag}${attrs}>${content}</${tag}>`;
    if (/\sid=("|').+?\1/.test(attrs)) return `<${tag}${attrs}>${content}</${tag}>`;
    return `<${tag}${attrs} id="${next.id}">${content}</${tag}>`;
  });
};

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const { h1, h2, h3, paragraph, list, listItem, code, note } = HELP_DOC_TYPOGRAPHY;

const HelpContent: React.FC<HelpContentProps> = ({ title, summary, body }) => {
  const headings = useMemo(() => parseHeadings(body), [body]);
  const html = useMemo(() => injectHeadingIds(toSafeHtml(body), headings), [body, headings]);
  const isHtmlBody = useMemo(() => looksLikeHtml(body), [body]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: HELP_DOC_TYPOGRAPHY.articlePadding,
        width: '100%',
        borderRadius: '14px !important',
        borderColor: HELP_DOC_THEME.border,
        bgcolor: HELP_DOC_THEME.contentBg,
        borderTop: `3px solid ${HELP_DOC_THEME.accent}`,
        fontFamily: HELP_DOC_THEME.fontBody,
        fontSize: '0.9375rem',
        lineHeight: HELP_DOC_TYPOGRAPHY.lineHeight,
        color: HELP_DOC_THEME.text,
        boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
      }}
    >
      <Typography component="h1" sx={{ ...h1, mt: 0 }}>
        {title}
      </Typography>
      {summary && (
        <Typography component="p" sx={{ ...paragraph, mt: 0, maxWidth: '100%' }}>
          {summary}
        </Typography>
      )}

      {headings.length > 0 && (
        <Box sx={{ ...note }}>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              marginBottom: '10px',
              fontSize: '0.875rem',
              color: HELP_DOC_THEME.text,
            }}
          >
            On this page
          </Typography>
          <Box
            component="ul"
            sx={{
              ...list,
              mb: 0,
              mt: 0,
              listStyle: 'disc',
            }}
          >
            {headings.map((h) => (
              <Box
                component="li"
                key={h.id}
                sx={{
                  ...listItem,
                  ml: h.level === 3 ? 2 : 0,
                  display: 'list-item',
                }}
              >
                <Typography
                  component="button"
                  onClick={() => scrollToSection(h.id)}
                  sx={{
                    border: 'none',
                    bgcolor: 'transparent',
                    p: 0,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.875rem',
                    lineHeight: 1.5,
                    color: HELP_DOC_THEME.link,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    '&:hover': { textDecoration: 'underline', color: HELP_DOC_THEME.accentDark },
                  }}
                >
                  {h.text}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          '& p': {
            ...paragraph,
          },
          '& p:first-of-type': {
            marginTop: 0,
          },
          '& strong': { fontWeight: 700, color: HELP_DOC_THEME.text },
          '& h2': {
            ...h2,
            scrollMarginTop: 48,
          },
          '& h2:first-of-type': {
            marginTop: HELP_DOC_TYPOGRAPHY.h2First.marginTop,
          },
          '& h3': {
            ...h3,
            scrollMarginTop: 48,
          },
          '& ul': {
            ...list,
            listStyle: 'disc',
          },
          '& li': {
            ...listItem,
            display: 'list-item',
          },
          '& code': {
            ...code,
          },
          '& a': {
            color: HELP_DOC_THEME.link,
            '&:hover': { color: HELP_DOC_THEME.accentDark },
          },
        }}
        dangerouslySetInnerHTML={{ __html: isHtmlBody ? html : `<p>${html}</p>` }}
      />
    </Paper>
  );
};

export default HelpContent;
